import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  catchError,
  finalize,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { HrisIntegrationProviderSelectionDialogComponent } from '../components/hris-integration-provider-selection-dialog/hris-integration-provider-selection-dialog.component';
import { HrisIntegrationService } from './hris-integration.service';
import {
  ProvidersDTO,
  HRISIntegrationDTO,
  Filter,
  HRISIntegrationPutDTO,
} from '../models/HRISIntegrationDTO';
import { CustomPortalError } from '../../../../core/errors/models/CustomPortalError';
import { OrganizationService } from './organization.service';
import { ErrorType } from '../../../../core/errors/models/ErrorType';
import { HrisIntegrationFiltersDialogComponent } from '../components/hris-integration-filters-dialog/hris-integration-filters-dialog.component';
import { DIALOG_SIZE_MEDIUM } from '../../../../shared/components/dialog/dialog-sizes';
import { LicenseCalculationsService } from '../../../payment/subscription/services/license-calculations.service';

@Injectable({
  providedIn: 'root',
})
export class HrisIntegrationDataService implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackbarService = inject(SnackbarService);
  private readonly licenseCalculationService = inject(
    LicenseCalculationsService,
  );
  private readonly hrisIntegrationService = inject(HrisIntegrationService);
  private readonly organizationService = inject(OrganizationService);

  private readonly destroy$: Subject<void> = new Subject();

  isLoadingHRISIntegration = false;
  isSyncingUsers = false;
  isCreatingHRISIntegration = false;
  isDeletingHRISIntegration = false;
  isObservingUrl = false;

  hrisIntegration: HRISIntegrationDTO | null = null;
  providers: ProvidersDTO[] = [];

  showProviders = 2;

  private readonly syncCompleted$: Subject<boolean> = new Subject();
  public syncCompletedObservable$ = this.syncCompleted$.asObservable();

  get filtersCount() {
    const filters = this.hrisIntegration?.filters ?? [];
    return filters.filter((f) => f.selected).length;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async setIntegration(hrisIntegration: HRISIntegrationDTO) {
    await this.resetQueryParams(['id', 'error', 'type', 'state']);
    this.hrisIntegration = {
      ...hrisIntegration,
      status: 'active',
    };
  }

  setNumberOfSyncedUsersOnIntegration(numberOfSyncedUsers: number) {
    if (this.hrisIntegration) {
      this.hrisIntegration.numberOfSyncedUsers = numberOfSyncedUsers;
    }
  }

  setLastSyncedAtOnIntegration(numberOfSyncedUsers: string) {
    if (this.hrisIntegration) {
      this.hrisIntegration.lastSyncedAt = numberOfSyncedUsers;
    }
  }

  init() {
    this.fetchProviders();
    this.isLoadingHRISIntegration = true;
    this.organizationService.getHRISIntegration().subscribe({
      next: async (hrisIntegrations) => {
        const hrisIntegration = hrisIntegrations?.[0];
        if (hrisIntegration) {
          await this.setIntegration(hrisIntegration);
        }
        this.isLoadingHRISIntegration = false;
      },
      error: (error: CustomPortalError) => {
        this.isLoadingHRISIntegration = false;
        if (error.errorType !== ErrorType.NOT_FOUND) {
          this.snackbarService.error(error.errorType);
        }
      },
    });
  }

  observeUrlAndSendIntegration(): void {
    if (this.isObservingUrl) {
      return;
    }
    this.isObservingUrl = true;
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (params) => {
        const id = params.get('id');
        const error = params.get('error');
        if (!id && !error) {
          //user is not trying to set up a hris integration
          //only reset if any HRIS integration params are present
          const currentParams = this.route.snapshot.queryParams;
          const hrisParams = ['id', 'error', 'type', 'state'];
          const hasHrisParam = hrisParams.some((key) => key in currentParams);
          if (hasHrisParam) {
            await this.resetQueryParams(hrisParams);
          }
          return;
        }
        if (error === 'access_denied') {
          // unified returned an error
          this.snackbarService.error(
            'portal.hrisIntegration.messages.accessDenied',
            false,
          );
          return;
        }
        if (!id) {
          // hrisIntegration id is missing for some reason
          this.snackbarService.error(
            'portal.hrisIntegration.messages.unifiedConnectionIdMissing',
            false,
          );
          return;
        }
        this.createHRISIntegration(id);
      });
  }

  async resetQueryParams(keysToRemove: string[]) {
    const currentParams = { ...this.route.snapshot.queryParams };
    const filteredParams = Object.fromEntries(
      Object.entries(currentParams).filter(
        ([key]) => !keysToRemove.includes(key),
      ),
    );
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: filteredParams,
      replaceUrl: true,
    });
  }

  async openHRISIntegrationDialog() {
    await this.resetQueryParams(['id', 'error', 'type', 'state']);
    this.dialog.open(HrisIntegrationProviderSelectionDialogComponent, {
      autoFocus: true,
      ...DIALOG_SIZE_MEDIUM,
    });
  }

  openFiltersDialog() {
    this.dialog.open(HrisIntegrationFiltersDialogComponent, {
      autoFocus: true,
      ...DIALOG_SIZE_MEDIUM,
    });
  }

  fetchProviders() {
    if (this.providers.length > 0) {
      return;
    }
    this.hrisIntegrationService.getProviders().subscribe({
      next: (providers) => {
        this.providers = providers;
      },
    });
  }

  createHRISIntegration(id: string) {
    this.isCreatingHRISIntegration = true;
    this.hrisIntegrationService.postHRISIntegration(id).subscribe({
      next: async (hrisIntegration) => {
        await this.setIntegration(hrisIntegration);
        this.isObservingUrl = false;
        this.snackbarService.success(
          'portal.hrisIntegration.messages.hrisIntegrationCreated',
        );
        this.isCreatingHRISIntegration = false;
      },
      error: () => {
        this.isCreatingHRISIntegration = false;
        this.snackbarService.error(
          'portal.hrisIntegration.messages.hrisIntegrationNotCreated',
          false,
        );
      },
    });
  }

  deleteIntegration() {
    if (!this.hrisIntegration) {
      return;
    }
    this.isDeletingHRISIntegration = true;
    this.hrisIntegrationService
      .deleteHRISIntegration(this.hrisIntegration.id)
      .subscribe({
        next: () => {
          this.hrisIntegration = null;
          this.isDeletingHRISIntegration = false;
          this.syncCompleted$.next(true);
          this.snackbarService.success(
            'portal.hrisIntegration.messages.hrisIntegrationDeleted',
          );
          this.fetchProviders();
        },
        error: () => {
          this.isDeletingHRISIntegration = false;
          this.snackbarService.error(
            'portal.hrisIntegration.messages.hrisIntegrationNotDeleted',
            false,
          );
        },
      });
  }

  saveFilters(filters: Filter[]) {
    if (!this.hrisIntegration) {
      return;
    }
    const data: HRISIntegrationPutDTO = {
      filters: filters.map(({ id, selected }) => ({
        id,
        selected,
      })),
    };
    this.isLoadingHRISIntegration = true;
    this.hrisIntegrationService
      .putHRISIntegration(this.hrisIntegration.id, data)
      .subscribe({
        next: async (hrisIntegration) => {
          await this.setIntegration(hrisIntegration);
          this.isLoadingHRISIntegration = false;
          this.snackbarService.success(
            'portal.hrisIntegration.messages.filtersUpdated',
          );
        },
        error: (e: CustomPortalError) => {
          this.isLoadingHRISIntegration = false;
          this.snackbarService.error(e.errorType);
        },
      });
  }

  sync() {
    if (!this.hrisIntegration) {
      return;
    }
    this.isSyncingUsers = true;
    this.hrisIntegrationService
      .sync(this.hrisIntegration.id)
      .pipe(
        // We handle the error already here instead in the subscribe because we have to depend on another `Observable`
        catchError(() =>
          this.licenseCalculationService.getValidAmountOfUsersToLicense().pipe(
            tap((validAmount) => {
              this.snackbarService.error(
                'portal.hrisIntegration.messages.syncError',
                false,
                { allowedNumberOfUsers: validAmount },
              );
            }),
            // rethrow an error to prevent `next` execution (error is already handled at this time)
            switchMap(() => throwError(() => undefined)),
          ),
        ),
        finalize(() => {
          this.isSyncingUsers = false;
          this.syncCompleted$.next(true);
        }),
      )
      .subscribe({
        next: (response) => {
          this.setNumberOfSyncedUsersOnIntegration(
            response.numberOfSyncedUsers,
          );
          this.setLastSyncedAtOnIntegration(response.lastSyncedAt);
          this.snackbarService.success(
            'portal.hrisIntegration.messages.syncSuccess',
            {
              numberOfSyncedUsers: response.numberOfSyncedUsers,
            },
          );
        },
      });
  }
}
